import React, { useEffect, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { useLocation } from 'react-router-dom'
import './ImageViewerListStyle.css'

import { fetchPatientFolder } from '@store/actions/folder.action'
import { fetchAllPatients } from '@store/actions/patientmanagement.action'
import { clearSelectedFolder } from '@store/reducers/folder.reducer'
import { clearSelectedPatient } from '@store/reducers/patientmanagement.reducer'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'

// Components
import ImageViewerListTaskbar from '@components/pages/ImageViewer/ImageViewerList/ImageViewerListTaskbar/ImageViewerListTaskbar.component'
import ImageViewerFolderList from '@components/pages/ImageViewer/ImageViewerList/ImageViewerFolderList/ImageViewerFolderList.component'
import ImageViewerListHeader from '../ImageViewerListHeader/ImageViewerListHeader.component'
import ImageViewerListGrid from '../ImageViewerListGrid/ImageViewerListGrid.component'
import ImageViewerListTable from '../ImageViewerListTable/ImageViewerListTable.component'

// Custom hooks
import { useImageViewerState } from './hooks/useImageViewerState'
import { useImageViewerActions } from './hooks/useImageViewerActions'

// Constants
const VIEW_TYPES = {
  GRID: 'grid',
  LIST: 'list',
} as const

type ViewType = (typeof VIEW_TYPES)[keyof typeof VIEW_TYPES]

function ImageViewerList(): React.ReactElement {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const { showLoading, hideLoading } = useGlobalLoadingActions()

  // Get the fromImageViewer flag from navigation state
  const { fromImageViewer: fromImageViewerState } = location.state || {
    fromImageViewer: false,
  }
  const shouldSkipFetch = fromImageViewerState

  // Custom hooks
  const {
    selectedFiles,
    view,
    searchTerm,
    favoritesOnly,
    searchFilters,
    setSelectedFiles,
    setView,
    setSearchTerm,
    setFavoritesOnly,
    setSearchFilters,
    selectedPatient,
    selectedFolder,
    childFolders,
    files,
    loading,
    hasSelectedFiles,
  } = useImageViewerState()

  const { handleViewFiles } = useImageViewerActions()

  const prevClinicIdRef = useRef<string | undefined>(undefined)

  // On clinic change: show global loading and fetch patients for the new clinic
  const clinicId = selectedClinic?.clinic_id
  useEffect(() => {
    const prevClinicId = prevClinicIdRef.current
    if (prevClinicId !== undefined && prevClinicId !== clinicId) {
      showLoading({ container: 'body' })
      dispatch(fetchAllPatients())
        .unwrap()
        .finally(() => {
          hideLoading('body')
        })
    }
    prevClinicIdRef.current = clinicId
  }, [clinicId, dispatch, showLoading, hideLoading])

  // Fetch patient folder when component mounts or when selectedPatient changes
  // Skip fetching if coming from ImageViewer to preserve current folder state
  useEffect(() => {
    if (selectedPatient?.patient_id && !shouldSkipFetch) {
      // Fetch the patient's main folder instead of all folders
      dispatch(fetchPatientFolder(selectedPatient.patient_id))
    }
  }, [
    dispatch,
    selectedPatient?.patient_id,
    shouldSkipFetch,
    selectedClinic?.clinic_id,
  ])

  // Clear folder and patient when clinic changes
  useEffect(() => {
    dispatch(clearSelectedFolder())
    if (
      selectedPatient?.patient_id &&
      selectedClinic?.clinic_id &&
      selectedPatient.clinic_id !== selectedClinic.clinic_id
    ) {
      dispatch(clearSelectedPatient())
    }
  }, [dispatch, selectedClinic?.clinic_id, selectedPatient])

  // Event handlers
  const handleToggleView = (newView: ViewType): void => {
    setView(newView)
  }

  const handleSearchChange = (term: string): void => {
    setSearchTerm(term)
  }

  const handleFavoritesOnlyChange = (showFavoritesOnly: boolean): void => {
    setFavoritesOnly(showFavoritesOnly)
  }

  const handleFilterChange = (filters: any): void => {
    setSearchFilters(filters)
  }

  const handleResetFilters = (): void => {
    setSearchFilters({})
    setSearchTerm('')
  }

  const handleViewFilesClick = (): void => {
    handleViewFiles(selectedFiles)
  }

  // Render methods
  const renderTaskbar = (): React.ReactElement => (
    <div>
      <ImageViewerListTaskbar
        onClickViewFiles={handleViewFilesClick}
        selectedFolder={selectedFolder}
        childFolders={childFolders}
        hasSelectedFiles={hasSelectedFiles}
      />
    </div>
  )

  const renderFolderList = (): React.ReactElement => (
    <div className="d-flex flex-column">
      <ImageViewerFolderList />
    </div>
  )

  const renderHeader = (): React.ReactElement => (
    <div>
      <ImageViewerListHeader
        view={view}
        onToggleView={handleToggleView}
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        favoritesOnly={favoritesOnly}
        onFavoritesOnlyChange={handleFavoritesOnlyChange}
        selectedCount={selectedFiles.length}
        totalCount={files?.length || 0}
        onSelectAll={() => {
          const filesToSelect = (files || []).map((file) => ({
            file_id: file.file_id,
            study_uid: file.study_uid,
            series_uid: file.series_uid,
          }))
          console.log('[onSelectAll] Selecting files:', {
            filesCount: files?.length || 0,
            filesToSelectCount: filesToSelect.length,
            fileIds: filesToSelect.map((f) => f.file_id),
          })
          setSelectedFiles(filesToSelect)
        }}
        onDeselectAll={() => setSelectedFiles([])}
        searchFilters={searchFilters}
        onFilterChange={handleFilterChange}
        onResetFilters={handleResetFilters}
        files={files || []}
      />
    </div>
  )

  const renderContent = (): React.ReactElement => {
    const commonProps = {
      selectedFiles,
      setSelectedFiles,
      searchTerm,
      favoritesOnly,
      files,
      loading,
      selectedFolder,
    }

    return (
      <div>
        {view === VIEW_TYPES.GRID ? (
          <ImageViewerListGrid {...commonProps} />
        ) : (
          <ImageViewerListTable {...commonProps} />
        )}
      </div>
    )
  }

  return (
    <>
      <div className="image-viewer-list p-0">
        {renderTaskbar()}
        {renderFolderList()}
        {renderHeader()}
        {renderContent()}
      </div>
    </>
  )
}

export default ImageViewerList
